import { useEffect, useMemo, useState, useCallback } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useSearchParams } from "react-router";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { useBrandId } from "../../hooks/useBrandId";
import { useStateValue } from "../../context/StateValueContext";
import { hasPermission } from "../../functions/permissionUtils";
import { PERMISSIONS } from "../../constants/permissions";
import VampMonthControl from "../../components/dashboard/vampRates/VampMonthControl";
import VampAccountList from "../../components/dashboard/vampRates/VampAccountList";
import VampDetailPanel from "../../components/dashboard/vampRates/VampDetailPanel";
import VampCaidBreakdown from "../../components/dashboard/vampRates/VampCaidBreakdown";
import VampMonthHistory from "../../components/dashboard/vampRates/VampMonthHistory";
import {
  caidKey,
  accountKey,
  accountUnmatched,
  isUnmatched,
  monthContext,
  resolveSelection,
  sortAccounts,
} from "../../components/dashboard/vampRates/vampStanding";
import type { VampDashboardResponse } from "../../types/vampRates";

dayjs.extend(utc);

const MONTH_PARAM = /^\d{4}-(0[1-9]|1[0-2])$/;

// The month to ask for. A month later than the server's own is not clamped here: the response
// names the month it actually ran, and that is what the page renders.
const resolveMonthParam = (param: string | null): string =>
  param !== null && MONTH_PARAM.test(param) ? param : dayjs.utc().format("YYYY-MM");

export default function VampRatesTab() {
  const { get, loading } = useApi();
  const [{ user }] = useStateValue();
  // Clients have no company picker in the header, so fall back to their own company.
  const { companyId: urlCompanyId } = useCompanyId();
  const companyId = urlCompanyId ?? user?.companyId ?? null;
  const { brandId } = useBrandId();

  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<VampDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const requestedMonth = resolveMonthParam(searchParams.get("month"));
  // The server decides which month it can serve, so the page follows its answer rather than the
  // browser's clock. Only "next" still reads the clock, and a fast one costs nothing: the request
  // it allows comes back as the current month.
  const month = data?.month ?? requestedMonth;
  const maxMonth = dayjs.utc().format("YYYY-MM");

  useEffect(() => {
    if (!companyId) {
      setData(null);
      return;
    }
    const params = new URLSearchParams({ month: requestedMonth, companyId: companyId.toString() });
    if (brandId) params.set("brandId", String(brandId));
    setData(null);
    setError(null);
    get<VampDashboardResponse>(`/api/vamp-rates/dashboard?${params.toString()}`)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to fetch VAMP rates"));
  }, [brandId, companyId, get, requestedMonth]);

  const setMonth = useCallback(
    (next: string) =>
      setSearchParams(
        (prev) => {
          prev.set("month", next);
          return prev;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const ctx = useMemo(() => monthContext(month, data?.today ?? dayjs.utc().format("YYYY-MM-DD")), [month, data?.today]);
  const accounts = useMemo(() => (data ? sortAccounts(data.accounts, month) : []), [data, month]);
  const selection = resolveSelection(accounts, selectedKey);

  if (!companyId) {
    const isAdmin = hasPermission(user, PERMISSIONS.VAMP_RATES_ALL);
    return (
      <Alert severity="info">
        {isAdmin ? "Select a company from the header to view VAMP rates." : "No company is associated with your account."}
      </Alert>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.25}>
      <VampMonthControl month={month} maxMonth={maxMonth} onChange={setMonth} />

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data && accounts.length === 0 && <Alert severity="info">No provider accounts for this company.</Alert>}

          {data && data.lastWrittenDate < `${month}-01` && (
            <Alert severity="info">Data for this period is still being gathered.</Alert>
          )}

          {selection && (
            <div className="grid items-start gap-4 xl:grid-cols-[560px_minmax(0,1fr)]">
              <VampAccountList
                accounts={accounts}
                ctx={ctx}
                selectedKey={selection.kind === "account" ? accountKey(selection.account) : caidKey(selection.caid)}
                onSelect={setSelectedKey}
              />
              <div className="flex flex-col gap-4">
                <VampDetailPanel selection={selection} ctx={ctx} />
                {selection.kind === "account" && <VampCaidBreakdown account={selection.account} ctx={ctx} />}
                <VampMonthHistory
                  history={selection.kind === "caid" ? selection.caid.history : selection.account.history}
                  ctx={ctx}
                  isCaid={selection.kind === "caid"}
                  unmatched={selection.kind === "caid" ? isUnmatched(selection.caid) : accountUnmatched(selection.account)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </Box>
  );
}
